import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ErrorSubject } from '../lib/api'

/** A fleet unit's page lives under its carrier's, so it needs the carrier too. */
const FLEET_PATHS: Readonly<Record<string, string>> = {
  Truck: 'trucks',
  Trailer: 'trailers',
  Driver: 'drivers',
}

/** Where a record lives on this portal, by the kind the API names it with. */
function recordPath(subject: ErrorSubject): string | null {
  switch (subject.kind) {
    case 'Broker':
      return `/brokers/${subject.id}`
    case 'Carrier':
      return `/carriers/${subject.id}`
    case 'TruckAvailability':
      return `/availability/${subject.id}`
    case 'Load':
      return `/loads/${subject.id}`
    // The rate confirmation already holding the same file (Stage 3, 3.1).
    case 'RateConfirmation':
      return `/rate-confirmations/${subject.id}`
    // An agent lives on its broker's page, so it is reachable only when the refusal
    // names the broker too — the load form's does; the agents panel's own, shown on
    // that very page, has nowhere else to go.
    case 'BrokerAgent':
      return subject.ownerId != null ? `/brokers/${subject.ownerId}` : null
    default: {
      const fleet = FLEET_PATHS[subject.kind]
      return fleet !== undefined && subject.ownerId != null
        ? `/carriers/${subject.ownerId}/${fleet}/${subject.id}`
        : null
    }
  }
}

/**
 * The way from a refused number to the record that holds it. "Correct the original
 * first, then register this one" (Hugo, TRKC-83) is a search of the book without
 * this, and one click with it. Renders nothing for a kind the portal has no screen
 * for — the sentence beside it still names the record.
 */
export function RecordLink({ subject }: { subject: ErrorSubject }) {
  const { t } = useTranslation()
  const path = recordPath(subject)

  if (path === null) {
    return null
  }

  return <Link to={path}>{t('registry.open', { name: subject.name })}</Link>
}
